'use client';

import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { createUserOfflineAvatar, updateUserOfflineAvatar } from '@/app/apis';
import { BaseLinePause } from '@/app/components/icons/BaseLinePause';
import { BaseLinePlay } from '@/app/components/icons/BaseLinePlay';
import { BaseLinePlus } from '@/app/components/icons/BaseLinePlus';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';
import { HeadlessImageUploader } from '@/app/components/shared/ImageUploader';
import { PhotoUploadTipsDialog } from '@/app/components/shared/PhotoUploadTipsDialog';
import { VoiceSelectionDialog } from '@/app/components/shared/VoiceSelectionDialog';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogTitle } from '@/app/components/ui/dialog';
import { toast } from '@/app/components/ui/use-toast';
import { IMG_DEFAULT } from '@/app/constants';
import { hasPhotoUploadTipsConsent, savePhotoUploadTipsConsent, useAudioPlay } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { ImageUploadFile } from '@/app/types';
import { cn } from '@/app/utils';
import type { StudioAvatarItem } from './avatar-gallery';

const MAX_IMAGE_FILE_SIZE = 50 * 1024 * 1024;
const MAX_CREATE_NAME_ZH = 10;
const MAX_CREATE_NAME_OTHER = 50;
const MAX_CREATE_DESCRIPTION = 5000;

const isQuotaExceeded = (error?: { code?: number; reason?: string }) =>
  error?.code === 429 || error?.reason === 'OfflineAvatarQuotaExceeded';

interface CreateAvatarDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (avatar: StudioAvatarItem) => void;
  editingAvatar?: StudioAvatarItem | null;
}

function VoicePreviewButton({ id, url, image }: { id: string; url: string; image: string }) {
  const tPlayer = useTranslations('tools.voiceClone.player');
  // useAudioPlay uses the playback id to coordinate globally. This preview and the
  // voice-library item can point at the same voice, but they own different Audio
  // elements, so keep their instance ids distinct to ensure one pauses the other.
  const { isPlaying, handlePlayPause } = useAudioPlay({ id: `offline-avatar-preview:${id}`, url });

  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        handlePlayPause();
      }}
      className="relative size-8 shrink-0 overflow-hidden rounded-full bg-system-white08 text-system-white"
      aria-label={tPlayer(isPlaying ? 'pause' : 'play')}
    >
      <img
        src={image || IMG_DEFAULT}
        alt=""
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute inset-0 size-full object-cover',
          isPlaying && 'animate-[spin_4s_linear_infinite]',
        )}
      />
      <span className="absolute inset-0 flex items-center justify-center bg-black/40 transition-colors hover:bg-black/55">
        {isPlaying ? <BaseLinePause className="size-4" /> : <BaseLinePlay className="size-4" />}
      </span>
    </button>
  );
}

export function CreateAvatarDialog({ open, onOpenChange, onCreated, editingAvatar }: CreateAvatarDialogProps) {
  const t = useTranslations('tools.offlineAvatar.avatar');
  const tUploadTips = useTranslations('vidu-stream.createMyOwn');
  const locale = useLocale();
  const userId = useStore((state) => state.userInfo?.id);
  const nameLimit = locale === 'zh' ? MAX_CREATE_NAME_ZH : MAX_CREATE_NAME_OTHER;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [voiceId, setVoiceId] = useState('');
  const [voiceName, setVoiceName] = useState('');
  const [voicePreviewUrl, setVoicePreviewUrl] = useState('');
  const [voiceImage, setVoiceImage] = useState('');
  const [imageFile, setImageFile] = useState<ImageUploadFile | null>(null);
  const [voiceDialogOpen, setVoiceDialogOpen] = useState(false);
  const [uploadTipsOpen, setUploadTipsOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const uploadRef = useRef<(() => void) | null>(null);

  const image = imageFile?.file?.selectedImage || imageFile?.file?.sourceImage || '';
  const imagePreview =
    imageFile?.file?.selectedImagePreview ||
    imageFile?.file?.sourceImagePreview ||
    imageFile?.file?.selectedImage ||
    '';
  const imageUploading = imageFile?.status === 'uploading';
  const canCreate = Boolean(name.trim() && image && !image.startsWith('temp://'));
  const editing = Boolean(editingAvatar);

  useEffect(() => {
    if (!open || !editingAvatar) return;
    setName(editingAvatar.name);
    setDescription(editingAvatar.description ?? '');
    setVoiceId(editingAvatar.voiceId ?? '');
    setVoiceName('');
    setVoicePreviewUrl('');
    setVoiceImage('');
    setImageFile({
      id: editingAvatar.id,
      status: 'done',
      file: {
        sourceImage: editingAvatar.src,
        sourceImagePreview: editingAvatar.src,
        sourceImageWidth: 0,
        sourceImageHeight: 0,
        selectedImage: editingAvatar.src,
        selectedImagePreview: editingAvatar.src,
      },
    });
  }, [editingAvatar, open]);

  const reset = () => {
    setName('');
    setDescription('');
    setVoiceId('');
    setVoiceName('');
    setVoicePreviewUrl('');
    setVoiceImage('');
    setImageFile(null);
    setVoiceDialogOpen(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (creating) return;
    onOpenChange(nextOpen);
    if (!nextOpen) reset();
  };

  const handleRequestUpload = (openFileSelector: () => void) => {
    if (hasPhotoUploadTipsConsent(userId)) {
      openFileSelector();
      return;
    }
    uploadRef.current = openFileSelector;
    setUploadTipsOpen(true);
  };

  const handleCreate = async () => {
    if (!image || image.startsWith('temp://')) {
      toast({ type: 'warning', message: t('missingCreateImage') });
      return;
    }
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast({ type: 'warning', message: t('missingCreateName') });
      return;
    }

    setCreating(true);
    try {
      const trimmedDescription = description.trim();
      const body = {
        name: trimmedName,
        live_image: image,
        ...(trimmedDescription ? { description: trimmedDescription } : {}),
        ...(voiceId ? { voice_id: voiceId } : {}),
      };
      const { data, error } = editingAvatar
        ? await updateUserOfflineAvatar({ path: { id: editingAvatar.id }, body: { id: editingAvatar.id, ...body } })
        : await createUserOfflineAvatar({ body });
      if (error || !data) {
        toast({
          type: 'error',
          message: editing ? t('updateFailed') : isQuotaExceeded(error) ? t('createQuotaExceeded') : t('createFailed'),
        });
        return;
      }

      onCreated({
        id: data.id,
        src: data.live_image,
        name: data.name,
        avatarId: data.id,
        description: data.description,
        voiceId: data.voice_id,
      });
      onOpenChange(false);
      reset();
      toast({ type: 'success', message: t(editing ? 'updateSuccess' : 'createSuccess') });
    } catch {
      toast({ type: 'error', message: t(editing ? 'updateFailed' : 'createFailed') });
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className="w-[836px] max-w-[calc(100%-32px)] border-white/10 bg-[#1c1c1c] p-6 text-white sm:w-[calc(100%-32px)]"
          closable={false}
          onClose={() => handleOpenChange(false)}
        >
          <div className="relative overflow-hidden">
            <div className="mb-5 flex items-center justify-between gap-3">
              <DialogTitle className="text-left text-white text-xl leading-none">
                {t(editing ? 'editTitle' : 'createTitle')}
              </DialogTitle>
              <DialogClose
                className="relative inset-auto top-auto right-auto flex size-7 shrink-0 text-white hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                onClick={() => handleOpenChange(false)}
                disabled={creating}
              />
            </div>

            <div className="flex gap-6 sm:flex-col">
              <div className="flex min-w-0 flex-1 flex-col gap-2 sm:w-full sm:flex-none">
                <HeadlessImageUploader
                  maxCount={1}
                  maxFileSize={MAX_IMAGE_FILE_SIZE}
                  allowTempMode={false}
                  emitUploadingPreview
                  disabled={creating}
                  onChange={(file) => setImageFile(file.status === 'error' || file.status === 'removed' ? null : file)}
                >
                  {({ openFileSelector }) => (
                    <button
                      type="button"
                      onClick={() => handleRequestUpload(openFileSelector)}
                      disabled={creating || imageUploading}
                      className={cn(
                        'flex h-[466px] w-full items-center justify-center overflow-hidden rounded-2xl border bg-white/5 transition-colors sm:aspect-square sm:h-auto',
                        imagePreview ? 'border-white/20' : 'border-white/10 hover:bg-white/[0.07]',
                      )}
                    >
                      {imagePreview ? (
                        <img src={imagePreview} alt="" className="size-full object-contain" />
                      ) : (
                        <span className="flex flex-col items-center gap-3">
                          <BaseLinePlus className="size-7 text-system-white" />
                          <span className="text-base text-white/40">{t('pickPhoto')}</span>
                        </span>
                      )}
                    </button>
                  )}
                </HeadlessImageUploader>
                <p className="text-center text-white/45 text-xs">{t('uploadCopyrightNotice')}</p>
              </div>

              <div className="flex w-[324px] shrink-0 flex-col gap-6 sm:w-full">
                <label className="block">
                  <div className="mb-2 flex items-center justify-between text-sm text-white/60">
                    <span>{t('nameLabel')}</span>
                    <span className="text-white/30 text-xs">
                      {name.length}/{nameLimit}
                    </span>
                  </div>
                  <input
                    value={name}
                    maxLength={nameLimit}
                    placeholder={t('namePlaceholder')}
                    className="h-12 w-full rounded-xl border border-white/10 bg-white/5 px-4 text-white outline-none transition-colors placeholder:text-white/40 focus:border-white/40 disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={creating}
                    onChange={(event) => setName(event.target.value)}
                  />
                </label>

                <label className="block">
                  <div className="mb-2 flex items-center justify-between text-sm text-white/60">
                    <span>{t('descriptionLabel')}</span>
                    <span className="text-white/30 text-xs">
                      {description.length}/{MAX_CREATE_DESCRIPTION}
                    </span>
                  </div>
                  <textarea
                    value={description}
                    maxLength={MAX_CREATE_DESCRIPTION}
                    rows={9}
                    placeholder={t('descriptionPlaceholder')}
                    className="w-full resize-none rounded-xl border border-white/10 bg-white/5 pt-3 pr-2 pb-3 pl-4 text-sm text-white outline-none transition-colors placeholder:text-white/40 focus:border-white/40 disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={creating}
                    onChange={(event) => setDescription(event.target.value)}
                  />
                </label>

                <div>
                  <p className="mb-2 text-sm text-white/60">{t('voiceLabel')}</p>
                  <VoiceSelectionDialog
                    open={voiceDialogOpen}
                    onOpenChange={setVoiceDialogOpen}
                    title={t('chooseVoice')}
                    showCustomVoices={false}
                    value={voiceId}
                    onConfirm={(nextVoiceId, nextVoice) => {
                      setVoiceId(nextVoiceId);
                      setVoiceName(nextVoice?.name || '');
                      setVoicePreviewUrl(nextVoice?.url || '');
                      setVoiceImage(nextVoice?.img || '');
                    }}
                    onResolvedVoice={(voice) => {
                      setVoiceName(voice.name);
                      setVoicePreviewUrl(voice.url || '');
                      setVoiceImage(voice.img || '');
                    }}
                    allowNoVoice
                    noSoundLabel={t('noVoice')}
                    voiceInfoEditFieldSuffix="offline-avatar"
                    trigger={
                      <div
                        className={cn(
                          'flex h-12 w-full items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-3 text-sm transition-colors hover:border-white/20',
                          voiceId ? 'text-white' : 'text-white/50 hover:text-white/70',
                        )}
                      >
                        {voiceId && voicePreviewUrl ? (
                          <VoicePreviewButton id={voiceId} url={voicePreviewUrl} image={voiceImage} />
                        ) : null}
                        <button
                          type="button"
                          className="flex min-w-0 flex-1 items-center gap-3 disabled:cursor-not-allowed"
                          disabled={creating}
                        >
                          <span className="min-w-0 flex-1 truncate text-left">{voiceName || t('chooseVoice')}</span>
                          <BaseLineRight className="size-4 shrink-0 text-white/40" />
                        </button>
                      </div>
                    }
                  />
                </div>

                <div className="mt-auto">
                  <Button
                    type="button"
                    variant="solid"
                    onClick={handleCreate}
                    loading={creating}
                    disabled={imageUploading || creating || !canCreate}
                    className="h-11 w-full rounded-xl bg-white px-8 font-medium text-black text-sm hover:bg-white disabled:opacity-50"
                  >
                    {t(editing ? 'save' : 'create')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <PhotoUploadTipsDialog
        open={uploadTipsOpen}
        title={tUploadTips('uploadTips.title')}
        orLabel={tUploadTips('uploadTips.or')}
        tip1Title={tUploadTips('uploadTips.tip1Title')}
        tip1Description={tUploadTips('uploadTips.tip1Description')}
        tip2Title={tUploadTips('uploadTips.tip2Title')}
        tip2Description={tUploadTips('uploadTips.tip2Description')}
        tip3Title={tUploadTips('uploadTips.tip3Title')}
        tip3Description={tUploadTips('uploadTips.tip3Description')}
        confirmText={tUploadTips('uploadTips.confirm')}
        onConfirm={() => {
          if (userId) savePhotoUploadTipsConsent(userId);
          setUploadTipsOpen(false);
          const openFileSelector = uploadRef.current;
          uploadRef.current = null;
          openFileSelector?.();
        }}
        onClose={() => {
          setUploadTipsOpen(false);
          uploadRef.current = null;
        }}
      />
    </>
  );
}
